import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
} from '../../../settings/store/services/details';
import {
  EditPSPWebhooksFormValues,
  PSPWebhooksKeys,
} from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';

const useWebhooks = () => {
  const {
    settings: {
      details: { validators, pspDetails },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPWebhooksFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm<EditPSPWebhooksFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty =
    !isEqual(fields, defaultFormValues) || Boolean(Object.keys(errors).length);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPWebhooksKeys;
    }[] = [];

    if (values.webHookUri !== defaultFormValues?.webHookUri) {
      updatedSettings.push({
        webhookUri: values.webHookUri,
        key: PSPWebhooksKeys.WEBHOOK_URI,
      });
    }

    const webHookEvents = pspDetails?.find(
      (item) => item.key === PSPWebhooksKeys.WEBHOOK_EVENTS,
    );

    if (webHookEvents) {
      updatedSettings.push({
        webhookEvents: {
          APPLICATION_CHANGE: values.applicationChange,
          ORGANIZATION_CHANGE: values.organizationChange,
          ORGANIZATION_CREATE: values.organizationCreate,
          TRANSACTION_CHANGE: values.transactionChange,
        },
        key: PSPWebhooksKeys.WEBHOOK_EVENTS,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));
      }

      setDefaultFormValues({
        applicationChange: values?.applicationChange || false,
        organizationChange: values?.organizationChange || false,
        organizationCreate: values?.organizationCreate || false,
        transactionChange: values?.transactionChange || false,
        webHookUri: values?.webHookUri || '',
      });
    }
  });

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const webhookEvents = pspDetails?.find(
        (item) => item.key === 'WEBHOOK_EVENTS',
      );
      const webHookUri = pspDetails?.find((item) => item.key === 'WEBHOOK_URI');

      reset({
        applicationChange:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.APPLICATION_CHANGE || false,
        organizationChange:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.ORGANIZATION_CHANGE || false,
        organizationCreate:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.ORGANIZATION_CREATE || false,
        transactionChange:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.TRANSACTION_CHANGE || false,
        webHookUri: (webHookUri?.data.value as string) || '',
      });

      setDefaultFormValues({
        applicationChange:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.APPLICATION_CHANGE || false,
        organizationChange:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.ORGANIZATION_CHANGE || false,
        organizationCreate:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.ORGANIZATION_CREATE || false,
        transactionChange:
          (webhookEvents?.data?.value as Record<string, boolean>)
            ?.TRANSACTION_CHANGE || false,
        webHookUri: (webHookUri?.data.value as string) || '',
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  return {
    isLoading,
    errorList,
    control,
    errors,
    onSubmit,
    fields,
    globalError,
    editIsLoading,
    isDirty,
  };
};

export default useWebhooks;
